'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Input } from '@/shared/ui/shadcn/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Button } from '@/shared/ui/shadcn/button';
import { ArrowUpDown, ListFilter, Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { SegmentedControl } from '@/shared/ui';
import type { HistorySortBy } from '../model/history.types';
import type { FilterType, SubtypeFilter } from '../hooks/use-history-filters';
import { FeedbackHeaderButton } from '@/features/support/ui/FeedbackHeaderButton';

interface HistoryFiltersProps {
  // Filter values
  filterType: FilterType;
  subtype: SubtypeFilter;
  searchQuery: string;
  selectedProjectId: string;
  sortBy: HistorySortBy;

  // Available options
  availableProjects: Array<{ id: string; name: string }>;
  // Resolved name of the actively-filtered building (for the on-screen chip).
  selectedProjectName: string;

  // Callbacks
  onFilterTypeChange: (type: FilterType) => void;
  onSubtypeChange: (subtype: SubtypeFilter) => void;
  onSearchChange: (query: string) => void;
  onProjectChange: (projectId: string) => void;
  onSortChange: (sort: HistorySortBy) => void;
  onClearFilters: () => void;

  // States
  hasActiveFilters: boolean;
}

export function HistoryFilters({
  filterType,
  subtype,
  searchQuery,
  selectedProjectId,
  sortBy,
  availableProjects,
  selectedProjectName,
  onFilterTypeChange,
  onSubtypeChange,
  onSearchChange,
  onProjectChange,
  onSortChange,
  onClearFilters,
  hasActiveFilters,
}: HistoryFiltersProps) {
  const t = useTranslations('history.filters');

  const tPage = useTranslations('history');

  // Search starts collapsed behind an icon; revealed on demand (or when a query
  // is already active, e.g. restored from the store). Kept open while a query
  // exists so the user can always see and clear it.
  const [isSearchOpen, setIsSearchOpen] = useState(Boolean(searchQuery));
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Listen for the global "/" shortcut (focuses `[data-search-input]`): open the
  // field first so there's something to focus.
  useEffect(() => {
    const input = searchInputRef.current;
    if (isSearchOpen) input?.focus();
  }, [isSearchOpen]);

  const toggleSearch = () => {
    setIsSearchOpen((open) => {
      const next = !open;
      // Collapsing discards the query — a hidden active filter is confusing.
      if (!next && searchQuery) onSearchChange('');
      return next;
    });
  };

  return (
    // Pinned page header (title + filter controls). `sticky` works here because
    // this block's containing block is the tall `max-w` page wrapper that holds
    // BOTH this header and the list below — so it stays pinned across the whole
    // scroll. (Sticking an inner row instead fails: its parent is only as tall
    // as the filters, so it unpins the moment you scroll past them.) Negative
    // margins bleed the blur across the page padding.
    <div className="sticky top-0 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-2 backdrop-blur-xl space-y-4">
      {/* Header row: title (leading) + filter/sort icon menus (trailing),
          mirroring Apple's nav bar. The subtype segmented control sits on its
          own row below (see further down). */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-page-title text-foreground">
          {tPage('pageTitle')}
        </h1>

        <div className="flex flex-wrap gap-3 items-center">
          <FeedbackHeaderButton />

          {/* Search toggle — reveals the search field below the header. */}
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('searchPlaceholder')}
            aria-expanded={isSearchOpen}
            onClick={toggleSearch}
            className={cn(searchQuery && 'text-primary')}
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Filter menu (mirrors Apple's `line.3.horizontal.decrease` toolbar
              item): icon-only trigger, tinted to the accent color while a filter
              is active. Filter type + project selection live inside the menu. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('filterMenuLabel')}
                className={cn(
                  filterType !== 'all' && 'text-primary'
                )}
              >
                <ListFilter className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuRadioGroup
                value={filterType}
                onValueChange={(value) =>
                  onFilterTypeChange(value as FilterType)
                }
              >
                <DropdownMenuRadioItem value="all">
                  {t('filterAll')}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="favorites">
                  {t('filterFavorites')}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>

              {/* By-building lives in a submenu so picking a project both sets
                  the filter type and the selected building in one step. */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className={cn(filterType === 'project' && 'text-primary')}
                >
                  {t('filterByProject')}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                  {availableProjects.length > 0 ? (
                    <DropdownMenuRadioGroup
                      value={
                        filterType === 'project' ? selectedProjectId : ''
                      }
                      onValueChange={(value) => {
                        if (filterType !== 'project') {
                          onFilterTypeChange('project');
                        }
                        onProjectChange(value);
                      }}
                    >
                      {availableProjects.map((project) => (
                        <DropdownMenuRadioItem
                          key={project.id}
                          value={project.id}
                        >
                          {project.name}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  ) : (
                    <DropdownMenuLabel className="text-list-item-subtitle font-normal text-muted-foreground">
                      {t('noProjects')}
                    </DropdownMenuLabel>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              {hasActiveFilters && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onClearFilters}>
                    {t('clear')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sort menu (mirrors Apple's `arrow.up.arrow.down` toolbar item). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('sortMenuLabel')}
              >
                <ArrowUpDown className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuRadioGroup
                value={sortBy}
                onValueChange={(value) =>
                  onSortChange(value as HistorySortBy)
                }
              >
                <DropdownMenuRadioItem value="date-desc">
                  {t('sortNewestFirst')}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="date-asc">
                  {t('sortOldestFirst')}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name-asc">
                  {t('sortNameAsc')}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name-desc">
                  {t('sortNameDesc')}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Subtype segmented control on its own row below the header, centered —
          mirrors Apple's segmented picker that sits under the nav bar. */}
      <div className="flex justify-center">
        <SegmentedControl
          options={[
            { id: 'all', label: t('subtypeAll') },
            { id: 'render', label: t('subtypeRenders') },
            { id: 'estimate', label: t('subtypeEstimates') },
            { id: 'album', label: t('subtypeAlbums') },
          ]}
          value={subtype}
          onChange={(value) => onSubtypeChange(value as SubtypeFilter)}
          size="md"
        />
      </div>

      {/* Active filter chip — makes a non-default filter (favorites / a specific
          building) visible on screen so users don't forget it's applied. The
          icon trigger tint alone is too easy to miss. Tapping the chip clears
          back to "All". */}
      {filterType !== 'all' && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => onFilterTypeChange('all')}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-list-item-subtitle"
            aria-label={t('clear')}
          >
            <span>
              {filterType === 'favorites'
                ? t('filterFavorites')
                : selectedProjectName || t('filterByProject')}
            </span>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Search field — revealed only when the search icon is toggled on. */}
      {isSearchOpen && (
        <div className="relative">
          <Search className="absolute start-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={t('searchPlaceholder')}
            className="ps-9 pe-10"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            data-search-input
          />
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('clear')}
            className="absolute end-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
            onClick={toggleSearch}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
